import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import QueueBar from "@/components/QueueBar";
import { markDone } from "@/lib/practiceQueue";
import DiffView from "@/components/DiffView";
import StatBox from "@/components/StatBox";
import CertificateBadge from "@/components/CertificateBadge";
import PersonalBestBadge from "@/components/PersonalBestBadge";
import LiveHighlighter from "@/components/LiveHighlighter";
import HighlightControlsPanel, { loadHighlightPrefs } from "@/components/HighlightControlsPanel";
import SourceScrollNudge from "@/components/SourceScrollNudge";
import KeyboardSoundToggle from "@/components/KeyboardSoundToggle";
import AdaptivePicks from "@/components/AdaptivePicks";
import { keyClickHandler } from "@/lib/keyboardSound";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { showBadgeUnlocks, showMistakesAddedToast } from "@/lib/badgeToast";
import { RotateCcw, ArrowLeft, Send, Timer, Play, X, Keyboard } from "lucide-react";

const DURATIONS = [15, 20, 25, 30, 45, 60];

const fmt = (s) => {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export default function TypingPractice() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [minutes, setMinutes] = useState(15);
  const [typed, setTyped] = useState("");
  const [started, setStarted] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [remaining, setRemaining] = useState(60);
  const [elapsed, setElapsed] = useState(0);
  const [locked, setLocked] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);
  const textareaRef = useRef(null);
  const onKey = useMemo(() => keyClickHandler(), []);

  useEffect(() => {
    api.get(`/typing-passages/${id}`).then((r) => setItem(r.data)).catch(() => toast.error("Not found"));
  }, [id]);

  const doSubmit = useCallback(async () => {
    if (submittedRef.current) return;
    if (!typed.trim()) { toast.error("Type something first"); return; }
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const seconds = startedAt ? (Date.now() - startedAt) / 1000 : minutes * 60;
      const { data } = await api.post("/attempts", {
        module: "typing", item_id: id, typed_text: typed, time_seconds: seconds,
      });
      setResult(data);
      markDone("typing", id);
      toast.success(`Accuracy ${data.accuracy_percent}% · ${data.wpm} WPM`);
      showBadgeUnlocks(data.new_badges);
      showMistakesAddedToast(data.mistakes_added);
    } catch { toast.error("Submission failed"); submittedRef.current = false; }
    finally { setSubmitting(false); }
  }, [typed, startedAt, id, minutes]);

  // Countdown
  useEffect(() => {
    if (!started || locked || result) return;
    const iv = setInterval(() => {
      const e = (Date.now() - startedAt) / 1000;
      setElapsed(e);
      const rem = minutes * 60 - e;
      setRemaining(rem);
      if (rem <= 0) {
        clearInterval(iv);
        setLocked(true);
        toast.info("Time's up! Auto-submitting…");
        setTimeout(() => doSubmit(), 300);
      }
    }, 250);
    return () => clearInterval(iv);
  }, [started, startedAt, minutes, locked, result, doSubmit]);

  const start = () => {
    setResult(null);
    setTyped("");
    submittedRef.current = false;
    setLocked(false);
    setStartedAt(Date.now());
    setElapsed(0);
    setRemaining(minutes * 60);
    setStarted(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const cancel = () => {
    setStarted(false);
    setLocked(false);
    setTyped("");
    setElapsed(0);
    setRemaining(minutes * 60);
    setStartedAt(null);
    submittedRef.current = false;
  };

  const reset = () => {
    setStarted(false);
    setTyped("");
    setResult(null);
    setElapsed(0);
    setLocked(false);
    setStartedAt(null);
    setRemaining(minutes * 60);
    submittedRef.current = false;
  };

  const [capsOn, setCapsOn] = useState(false);
  const [highlightPrefs, setHighlightPrefs] = useState(() => loadHighlightPrefs());
  const sourceScrollRef = useRef(null);
  const handleType = (e) => {
    if (locked) return;
    setTyped(e.target.value);
  };

  const typedWords = typed.trim() ? typed.trim().split(/\s+/).length : 0;
  // Only show Live WPM after 3s to avoid the 800+ WPM flash on first keystroke
  const liveWpm = started && elapsed >= 3 ? Math.round(typedWords / (elapsed / 60)) : null;
  const timerLow = started && remaining <= 30 && remaining > 0;
  const isHindi = item?.language === "hi";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <QueueBar module="typing" currentId={id} hrefFor={(nid) => `/typing/${nid}`} />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/typing" className="text-xs uppercase tracking-[0.3em] font-bold text-muted-foreground hover:text-brand-gold" data-testid="back-to-typing">
          <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Back to Passages
        </Link>

        {!item ? <div className="mt-8 font-mono text-muted-foreground">Loading…</div> : (
          <>
            <div className="mt-4 mb-6 flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold flex items-center gap-2">
                  <Keyboard className="w-3.5 h-3.5" /> {isHindi ? "हिन्दी टाइपिंग" : "English Typing"} · {item.word_count} words
                </div>
                <h1 className="text-3xl lg:text-4xl font-black tracking-tight mt-2" data-testid="typing-title">{item.title}</h1>
              </div>
              {!started && !result && (
                <Button
                  onClick={start}
                  className="rounded-lg h-11 bg-brand-crimson hover:bg-red-700 text-white text-xs uppercase tracking-[0.2em] font-bold"
                  data-testid="typing-start-btn"
                >
                  <Play className="w-4 h-4 mr-2" /> Start {minutes}-min test
                </Button>
              )}
              {started && !locked && !result && (
                <div className="flex items-center gap-3">
                  <div
                    className={`inline-flex items-center gap-2 border-2 px-4 py-2 font-mono text-2xl font-black ${
                      timerLow ? "border-red-500 bg-red-50 text-red-700 animate-pulse" : "border-brand-gold text-foreground"
                    }`}
                    data-testid="typing-timer"
                  >
                    <Timer className="w-5 h-5" /> {fmt(Math.max(0, remaining))}
                  </div>
                  <Button onClick={cancel} variant="outline" className="rounded-lg h-11 border-border" data-testid="typing-cancel-btn">
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                </div>
              )}
            </div>

            {/* Duration selector */}
            {!started && !result && (
              <div className="mb-6" data-testid="typing-duration-panel">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground mb-3">Choose duration</div>
                <div className="flex flex-wrap gap-2">
                  {DURATIONS.map((d) => (
                    <button
                      key={d}
                      onClick={() => setMinutes(d)}
                      data-testid={`typing-duration-${d}`}
                      className={`min-w-[64px] px-4 py-3 border rounded-lg font-mono text-lg font-black transition-all ${
                        minutes === d ? "border-brand-gold bg-brand-gold text-background" : "border-border bg-secondary/40 text-foreground hover:border-brand-gold/40 hover:bg-secondary"
                      }`}
                    >
                      {d} <span className="text-[10px] block font-bold uppercase tracking-widest">min</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {locked && !result && (
              <div className="mb-4 border-l-4 border-brand-crimson bg-brand-crimson/10 rounded-lg px-4 py-3 font-mono text-sm text-brand-crimson" data-testid="typing-timeup">
                ⏰ Time's up — typing locked, auto-submitting…
              </div>
            )}

            {/* Live counters */}
            {(started || result) && (
              <div className="grid grid-cols-4 gap-3 mb-6">
                <StatBox testId="typing-elapsed" label="Elapsed" value={elapsed.toFixed(1)} unit="s" tone="default" />
                <StatBox testId="typing-live-wpm" label="Live WPM" value={liveWpm ?? "—"} unit={liveWpm != null ? "wpm" : "warm-up"} tone="accent" />
                <StatBox testId="typing-words-typed" label="Words typed" value={typedWords} tone="default" />
                <StatBox testId="typing-target" label="Duration" value={minutes} unit="min" tone="default" />
              </div>
            )}

            {/* Source passage — live-highlighted while typing */}
            <div ref={sourceScrollRef} className="relative border border-border bg-secondary/60 p-6 mb-4 max-h-72 overflow-y-auto" data-testid="typing-source-container">
              <SourceScrollNudge targetRef={sourceScrollRef} testId="typing-source-nudge" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2 flex items-center justify-between">
                <span>Source text</span>
                {started && !result && (
                  <span className="normal-case tracking-normal text-muted-foreground/70">
                    Live: <span className="text-green-700 font-bold">correct</span> ·{" "}
                    <span className="text-red-700 font-bold">wrong</span> ·{" "}
                    <span className="text-yellow-800 font-bold">typing…</span>
                  </span>
                )}
              </div>
              {started || result ? (
                <LiveHighlighter
                  source={item.content}
                  typed={typed}
                  lang={item.language}
                  wordHighlight={highlightPrefs.wordHighlight}
                  mistakeHighlight={highlightPrefs.mistakeHighlight}
                  autoScroll={highlightPrefs.autoScroll}
                />
              ) : (
                <div
                  className={`whitespace-pre-wrap practice-source-text ${isHindi ? "is-hindi" : "font-mono"}`}
                  lang={item.language}
                  data-testid="typing-source-text"
                >
                  {item.content}
                </div>
              )}
            </div>

            <div className="flex justify-end mb-2 gap-2 flex-wrap">
              <HighlightControlsPanel value={highlightPrefs} onChange={setHighlightPrefs} testId="typing-highlight-controls" />
              {capsOn && (
                <span data-testid="typing-capslock-indicator" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-brand-gold bg-brand-gold/10 text-brand-gold text-[10px] uppercase tracking-widest font-bold">
                  Caps Lock ON
                </span>
              )}
              <KeyboardSoundToggle />
            </div>

            <Textarea
              ref={textareaRef}
              value={typed}
              onChange={handleType}
              onKeyDown={(e) => { if (e.getModifierState) setCapsOn(e.getModifierState("CapsLock")); onKey(e); }}
              onKeyUp={(e) => { if (e.getModifierState) setCapsOn(e.getModifierState("CapsLock")); }}
              disabled={!started || locked}
              placeholder={
                started
                  ? (isHindi ? "यहाँ टाइप करना शुरू करें…" : "Start typing here…")
                  : "Choose a duration and click Start to begin"
              }
              className={`min-h-[280px] rounded-lg border-2 border-brand-gold/30 bg-white text-[#0F172A] placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:border-brand-gold practice-typing-area font-bold ${
                isHindi ? "is-hindi" : "font-mono"
              } ${(!started || locked) ? "bg-slate-100 cursor-not-allowed opacity-90" : ""}`}
              lang={item.language}
              data-testid="typing-textarea"
            />

            <div className="flex flex-wrap gap-3 mt-6">
              {started && !locked && !result && (
                <Button
                  onClick={doSubmit}
                  disabled={submitting}
                  className="rounded-lg h-12 px-8 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold"
                  data-testid="typing-finish-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> {submitting ? "Checking…" : "Finish early"}
                </Button>
              )}
              {(result || locked) && (
                <Button
                  onClick={reset}
                  variant="outline"
                  className="rounded-lg h-12 px-6 border-border"
                  data-testid="typing-reset-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> New attempt
                </Button>
              )}
            </div>

            {result && (
              <section className="mt-12" data-testid="typing-result">
                <h2 className="text-2xl font-black tracking-tight mb-6">Results</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <StatBox testId="typing-result-accuracy" label="Accuracy" value={result.accuracy_percent} unit="%" tone={result.accuracy_percent >= 90 ? "good" : result.accuracy_percent >= 70 ? "warn" : "bad"} />
                  <StatBox testId="typing-result-errors" label="Errors" value={result.error_percent} unit="%" tone="bad" />
                  <StatBox testId="typing-result-wpm" label="WPM" value={result.wpm} unit="wpm" tone="accent" />
                  <StatBox testId="typing-result-words" label="Words" value={`${result.correct}/${result.total_words}`} tone="default" />
                </div>
                <CertificateBadge attemptId={result.id} accuracy={result.accuracy_percent} wpm={result.wpm} />
                <PersonalBestBadge result={result} />
                <div className="mt-8">
                  <DiffView result={result.diff} />
                </div>
                <div className="mt-10">
                  <AdaptivePicks module="typing" limit={3} title="Try next" />
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
